/**
 * InvitePage.js - 방 초대 링크 처리 컴포넌트
 *
 * /invite/:inviteCode 라우트에서 렌더링됨.
 * - 로그인 된 경우: 즉시 해당 방 참여 시도 → 성공 시 /groups로 이동
 * - 로그인 안 된 경우 (Android + 앱 미설치): 앱 설치 유도 화면 → 이후 /auth?mode=register
 * - 로그인 안 된 경우 (기타): pendingInviteCode 저장 후 /auth?mode=register 이동
 * - 인앱 브라우저 감지 시 상단에 Safari/Chrome으로 열기 안내 배너 표시
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../config/firebaseConfig';
import { API_BASE_URL } from '../config/apiConfig';

const API_BASE = API_BASE_URL;

const detectInAppBrowser = () => {
  const ua = navigator.userAgent;
  if (/NAVER|KAKAOTALK|Instagram|FBAN|FBAV|Line\/|MicroMessenger/i.test(ua)) return true;
  if (/iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua) && !/CriOS\//.test(ua)) return true;
  return false;
};

const isIOSDevice = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const isAndroidDevice = () => /Android/.test(navigator.userAgent);
const isStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const InvitePage = ({ installPrompt: installPromptProp, onInstallPromptUsed }) => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  // checking: auth 확인 중 | joining: 방 참여 중 | error: 오류 | install: 설치 유도
  const [status, setStatus] = useState('checking');
  const [statusMsg, setStatusMsg] = useState('인증 확인 중...');
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [localInstallPrompt, setLocalInstallPrompt] = useState(null);
  const pendingSavedRef = useRef(false);

  const inAppBrowser = detectInAppBrowser();
  const iosDevice = isIOSDevice();

  // InvitePage 자체도 beforeinstallprompt 캡처 (App.js보다 먼저 뜰 수 있음)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setLocalInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const goToAuth = useCallback(() => {
    navigate(`/auth?mode=register&invite=${encodeURIComponent(inviteCode)}`, { replace: true });
  }, [navigate, inviteCode]);

  const joinRoom = useCallback(async (currentUser) => {
    try {
      setStatus('joining');
      setStatusMsg('방에 참여하는 중...');
      const token = await currentUser.getIdToken();

      const res = await fetch(`${API_BASE}/api/coordination/rooms/${inviteCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (res.ok) {
        const roomId = data.room?._id || data._id;
        if (roomId) {
          navigate(`/groups?openRoom=${encodeURIComponent(roomId)}`, { replace: true });
        } else {
          navigate('/groups', { replace: true });
        }
      } else {
        setErrorMsg(data.msg || '방 참여에 실패했습니다.');
        setStatus('error');
      }
    } catch (e) {
      setErrorMsg(e.message || '오류가 발생했습니다.');
      setStatus('error');
    }
  }, [inviteCode, navigate]);

  useEffect(() => {
    if (!inviteCode) {
      navigate('/', { replace: true });
      return;
    }

    (async () => {
      try {
        // Firebase 인증 복원 대기 (최대 5초)
        let currentUser = auth.currentUser;
        if (!currentUser) {
          await new Promise((resolve) => {
            const timer = setTimeout(() => resolve(), 5000);
            const unsub = auth.onAuthStateChanged((u) => {
              clearTimeout(timer);
              unsub();
              resolve();
            });
          });
          currentUser = auth.currentUser;
        }

        if (!currentUser) {
          // pendingInviteCode 저장 → 로그인 후 isLoggedIn effect에서 처리
          if (!pendingSavedRef.current) {
            localStorage.setItem('pendingInviteCode', inviteCode);
            pendingSavedRef.current = true;
          }
          goToAuth();
          return;
        }

        // 로그인 상태 → 즉시 방 참여
        joinRoom(currentUser);
      } catch (e) {
        setErrorMsg(e.message || '오류가 발생했습니다.');
        setStatus('error');
      }
    })();
  }, [inviteCode, navigate, joinRoom, goToAuth]); // installPromptProp/localInstallPrompt는 이 effect에서 미사용 → 불필요한 재실행 방지

  const handleInstall = async () => {
    const prompt = installPromptProp || localInstallPrompt;
    if (!prompt) {
      // installPrompt 없음 → Chrome 메뉴 통해 수동 설치 안내 (토스트 대신 alert)
      alert('Chrome 주소창 오른쪽 메뉴(⋮)를 누른 뒤 "홈 화면에 추가"를 선택해주세요.');
      return;
    }
    setInstalling(true);
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        onInstallPromptUsed?.();
      }
    } catch (e) {
      console.warn('[InvitePage] install prompt error:', e);
    }
    // 설치 수락/거절 관계없이 회원가입으로 이동
    goToAuth();
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const banner = inAppBrowser ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
      background: '#f59e0b', color: '#1c1917',
      padding: '12px 16px', fontSize: '13px', fontWeight: 600,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)', textAlign: 'center',
      lineHeight: '1.6',
    }}>
      📱 앱 내 브라우저로 열렸습니다.
      {iosDevice ? (
        <>
          <br/>
          <span style={{ fontWeight: 400, fontSize: '12px' }}>
            홈화면 앱을 열려면 링크를 복사 후 <strong>Safari</strong>에서 붙여넣으세요.
          </span>
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={handleCopyLink}
              style={{
                background: copied ? '#16a34a' : '#1c1917', color: '#fff', border: 'none',
                borderRadius: '6px', padding: '6px 16px', fontSize: '12px',
                fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s',
              }}
            >
              {copied ? '✓ 복사됨!' : '링크 복사'}
            </button>
          </div>
        </>
      ) : (
        <>
          <br/>
          <span style={{ fontWeight: 400, fontSize: '12px' }}>
            홈화면 앱을 열려면 <strong>Chrome</strong>으로 열어주세요.
          </span>
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={() => window.open(window.location.href, '_blank')}
              style={{
                background: '#1c1917', color: '#fff', border: 'none',
                borderRadius: '6px', padding: '6px 16px', fontSize: '12px',
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              Chrome으로 열기
            </button>
          </div>
        </>
      )}
    </div>
  ) : null;

  const containerStyle = {
    ...styles.container,
    paddingTop: inAppBrowser ? '120px' : 0,
  };

  // ── 앱 설치 유도 화면 ──────────────────────────────────────────────────────
  if (status === 'install') {
    const hasPrompt = !!(installPromptProp || localInstallPrompt);
    return (
      <div style={containerStyle}>
        {banner}
        <div style={styles.card}>
          {/* 앱 아이콘 영역 */}
          <div style={styles.appIconWrap}>
            <div style={styles.appIcon}>📅</div>
          </div>
          <h2 style={styles.installTitle}>초대장을 받으셨군요!</h2>
          <p style={styles.installDesc}>
            앱을 설치하면 초대받은 방에 바로 입장하고,
            <br />일정 조율 알림도 받을 수 있어요.
          </p>

          {/* 설치 버튼 */}
          <button
            style={{
              ...styles.primaryBtn,
              background: installing ? '#93c5fd' : '#3b82f6',
              cursor: installing ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
            onClick={handleInstall}
            disabled={installing}
          >
            {installing ? (
              <>
                <span style={styles.miniSpinner} />
                설치 중...
              </>
            ) : hasPrompt ? (
              <>📲 앱 설치하기</>
            ) : (
              <>📲 홈 화면에 추가하기</>
            )}
          </button>

          {/* prompt 없을 때 안내 */}
          {!hasPrompt && (
            <p style={{ ...styles.installNote, color: '#94a3b8', background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', textAlign: 'left', lineHeight: '1.7' }}>
              💡 Chrome 메뉴(⋮) → <strong>"홈 화면에 추가"</strong>를 눌러도 설치할 수 있어요.
            </p>
          )}

          {/* 스킵 버튼 */}
          <button
            style={styles.skipBtn}
            onClick={goToAuth}
          >
            그냥 브라우저로 계속하기
          </button>

          <p style={styles.installNote}>
            설치 후에도 회원가입이 필요합니다.
            <br />초대 코드는 자동으로 유지됩니다.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'checking' || status === 'joining') {
    return (
      <div style={containerStyle}>
        {banner}
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={styles.text}>{statusMsg}</p>
          {status === 'joining' && (
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>초대 코드: {inviteCode}</p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={containerStyle}>
        {banner}
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <p style={styles.errorText}>{errorMsg}</p>
          <button style={styles.primaryBtn} onClick={() => navigate('/groups', { replace: true })}>
            그룹으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return null;
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    background: '#fff',
    borderRadius: '24px',
    padding: '40px 32px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
    maxWidth: '340px',
    width: '90%',
  },
  appIconWrap: {
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(59,130,246,0.3)',
  },
  appIcon: {
    fontSize: '40px',
  },
  installTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    color: '#1e293b',
    textAlign: 'center',
  },
  installDesc: {
    margin: 0,
    fontSize: '14px',
    color: '#64748b',
    textAlign: 'center',
    lineHeight: '1.7',
  },
  installNote: {
    margin: 0,
    fontSize: '12px',
    color: '#94a3b8',
    textAlign: 'center',
    lineHeight: '1.6',
  },
  skipBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '4px 0',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  miniSpinner: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.4)',
    borderTop: '2px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  text: {
    color: '#475569',
    fontSize: '15px',
    margin: 0,
  },
  primaryBtn: {
    padding: '13px 24px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  errorIcon: {
    width: '48px',
    height: '48px',
    background: '#fee2e2',
    color: '#dc2626',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '14px',
    textAlign: 'center',
    margin: 0,
  },
};

export default InvitePage;
